import React, { useState, useEffect, useRef } from 'react';
import { ApiService } from '../services/apiService';
import { INITIAL_BOARD } from '../game/types';
import { makeMove, copyBoard } from '../game/logic/board';
import { checkForIgo } from '../game/logic/igo';
import type { Cell } from '../game/types';
import './GameReplay.css';

interface GameReplayProps {
  gameId: string;
  onClose: () => void;
  opponent: string;
  result: 'win' | 'loss' | 'draw';
  playerColor: 'white' | 'black';
  timestamp: string;
  winType?: 'igo' | 'wego' | null;
  gameEndReason?: string | null;
}

interface Move {
  row: number;
  col: number;
  player: 'white' | 'black';
  yugos?: number;
}

// Helper function to format game timestamps in user's local timezone
// SQLite stores timestamps in UTC, so we need to parse them as UTC and convert to local
const formatGameTimestamp = (timestamp: string, format: 'date' | 'time' | 'datetime' = 'datetime'): string => {
  if (!timestamp) return '';
  
  let date: Date;
  
  // If timestamp doesn't have timezone info, assume it's UTC (SQLite format)
  // SQLite format: "YYYY-MM-DD HH:MM:SS"
  if (timestamp.includes('T') || timestamp.includes('Z') || timestamp.includes('+')) {
    // Already has timezone info (ISO format)
    date = new Date(timestamp);
  } else {
    // SQLite format without timezone - treat as UTC
    // Replace space with 'T' and append 'Z' for ISO format (more reliable on mobile)
    const isoFormat = timestamp.replace(' ', 'T') + 'Z';
    date = new Date(isoFormat);
    
    // Fallback if ISO format doesn't work
    if (isNaN(date.getTime())) {
      // Try with UTC suffix (some browsers support this)
      date = new Date(timestamp + ' UTC');
    }
    
    // Final fallback - parse manually
    if (isNaN(date.getTime())) {
      const parts = timestamp.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
      if (parts) {
        // Create date in UTC: year, month (0-indexed), day, hour, minute, second
        date = new Date(Date.UTC(
          parseInt(parts[1], 10),
          parseInt(parts[2], 10) - 1,
          parseInt(parts[3], 10),
          parseInt(parts[4], 10),
          parseInt(parts[5], 10),
          parseInt(parts[6], 10)
        ));
      }
    }
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.error('Invalid date:', timestamp);
    return 'Invalid Date';
  }
  
  // Format based on requested format
  if (format === 'date') {
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } else if (format === 'time') {
    return date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } else {
    return date.toLocaleString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }
};

const GameReplay: React.FC<GameReplayProps> = ({
  gameId,
  onClose,
  opponent,
  result,
  playerColor,
  timestamp,
  winType,
  gameEndReason
}) => {
  const [moves, setMoves] = useState<Move[]>([]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState<number>(-1);
  const currentMoveIndexRef = useRef<number>(-1);
  const [board, setBoard] = useState<(Cell | null)[][]>(copyBoard(INITIAL_BOARD));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [igoCells, setIgoCells] = useState<Set<string>>(new Set());
  const [historyHoverIndex, setHistoryHoverIndex] = useState<number | null>(null);
  const [holdScrollInterval, setHoldScrollInterval] = useState<NodeJS.Timeout | null>(null);
  const holdStartTimeRef = useRef<number | null>(null);
  const holdScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeMoveRef = useRef<HTMLDivElement | null>(null);
  const [copyNotification, setCopyNotification] = useState<string | null>(null);

  useEffect(() => {
    loadGameReplay();
  }, [gameId]);

  useEffect(() => {
    // Update ref when currentMoveIndex changes
    currentMoveIndexRef.current = currentMoveIndex;
  }, [currentMoveIndex]);

  useEffect(() => {
    // Reconstruct board up to current move
    reconstructBoard();
  }, [currentMoveIndex, moves]);

  // Auto-scroll active move into view
  useEffect(() => {
    if (activeMoveRef.current) {
      activeMoveRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [currentMoveIndex]);

  const getNotation = (col: number, row: number): string => {
    // Ensure values are numbers - use Number() for robust conversion
    const colNum = Number(col);
    const rowNum = Number(row);
    
    // Validate ranges (0-7 for 8x8 board)
    if (isNaN(colNum) || colNum < 0 || colNum > 7 || !Number.isInteger(colNum)) {
      console.warn('Invalid col:', col, '->', colNum);
      return '??';
    }
    if (isNaN(rowNum) || rowNum < 0 || rowNum > 7 || !Number.isInteger(rowNum)) {
      console.warn('Invalid row:', row, '->', rowNum);
      return '??';
    }
    
    const colLetter = String.fromCharCode(97 + colNum).toUpperCase(); // A-H
    const rowNumber = 8 - rowNum; // 8-1 (rowNum is 0-7, so rowNumber is 8-1)
    
    // Final validation - rowNumber should be 1-8
    if (rowNumber < 1 || rowNumber > 8) {
      console.error('Calculated invalid rowNumber:', rowNumber, 'from row:', rowNum, 'col:', colNum);
      return '??';
    }
    
    return colLetter + String(rowNumber); // Explicitly convert to string
  };

  const loadGameReplay = async () => {
    try {
      setLoading(true);
      setError(null);
      const { game, error: gameError } = await ApiService.getGameReplay(gameId);
      
      if (gameError) {
        setError(gameError);
        return;
      }

      if (game && game.moves) {
        // Debug: log raw moves data
        console.log('Raw moves from API:', game.moves);
        console.log('First move details:', game.moves[0]);
        
        // Ensure move row/col are numbers and validate
        const normalizedMoves = game.moves.map((move: any, index: number) => {
          let row = typeof move.row === 'string' ? parseInt(move.row, 10) : Number(move.row);
          let col = typeof move.col === 'string' ? parseInt(move.col, 10) : Number(move.col);
          
          // Check if values are in display format (1-8) instead of array index (0-7)
          // If row is > 7, it might be in display format, convert it
          if (row > 7 && row <= 8) {
            console.warn(`Move ${index}: Row appears to be in display format (${row}), converting to array index`);
            row = 8 - row; // Convert display format (1-8) to array index (0-7)
          }
          if (col > 7 && col <= 8) {
            console.warn(`Move ${index}: Col appears to be in display format (${col}), converting to array index`);
            col = 8 - col; // Convert display format (1-8) to array index (0-7)
          }
          
          // Debug: log first few moves with full details
          if (index < 5) {
            const testNotation = getNotation(col, row);
            console.log(`Move ${index}:`, {
              'move object': move,
              'row raw': move.row,
              'row type': typeof move.row,
              'row normalized': row,
              'col raw': move.col,
              'col type': typeof move.col,
              'col normalized': col,
              'has yugos': move.yugos !== undefined,
              'yugos value': move.yugos,
              'calculated notation': testNotation,
              '8 - row': 8 - row,
              'colLetter calc': String.fromCharCode(97 + col).toUpperCase(),
              'rowNumber calc': 8 - row
            });
          }
          
          // Validate ranges (0-7 for 8x8 board)
          if (isNaN(row) || row < 0 || row > 7 || isNaN(col) || col < 0 || col > 7) {
            console.error(`Invalid move at index ${index}:`, move, { row, col });
            // Try to fix if it's just slightly out of range
            if (row === 8) row = 0;
            if (col === 8) col = 0;
            if (row < 0) row = 0;
            if (col < 0) col = 0;
            if (row > 7) row = 7;
            if (col > 7) col = 7;
          }
          
          return {
            ...move,
            row,
            col
          };
        });
        setMoves(normalizedMoves);
        const startIndex = normalizedMoves.length - 1;
        setCurrentMoveIndex(startIndex); // Start at last move
        currentMoveIndexRef.current = startIndex;
      } else {
        setError('No moves found for this game');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load game replay');
    } finally {
      setLoading(false);
    }
  };


  const reconstructBoard = () => {
    const newBoard = copyBoard(INITIAL_BOARD);
    const igoSet = new Set<string>();
    
    // Apply moves up to currentMoveIndex
    for (let i = 0; i <= currentMoveIndex && i < moves.length; i++) {
      const move = moves[i];
      const updatedBoard = makeMove(newBoard, move.row, move.col, move.player);
      // Copy the updated board
      for (let r = 0; r < 8; r++) {
        for (let c = 0; c < 8; c++) {
          newBoard[r][c] = updatedBoard[r][c];
        }
      }
      
      // Check for Igo after this move
      const igoLine = checkForIgo(newBoard, move.row, move.col, move.player);
      if (igoLine) {
        igoLine.forEach(cell => {
          igoSet.add(`${cell.row},${cell.col}`);
        });
      }
    }
    
    setBoard(newBoard);
    setIgoCells(igoSet);
  };

  const goToMove = (index: number) => {
    if (index >= -1 && index < moves.length) {
      setCurrentMoveIndex(index);
      currentMoveIndexRef.current = index;
    }
  };

  const goToStart = () => goToMove(-1);
  const goToPrevious = () => goToMove(currentMoveIndex - 1);
  const goToNext = () => goToMove(currentMoveIndex + 1);
  const goToEnd = () => goToMove(moves.length - 1);

  const formatMoveHistoryAsText = (): string => {
    if (moves.length === 0) return '';
    
    const totalPairs = Math.ceil(moves.length / 2);
    const numWidth = String(totalPairs).length;
    
    const lines: string[] = [];
    for (let i = 0; i < moves.length; i += 2) {
      const moveNumber = Math.floor(i / 2) + 1;
      const whiteMove = moves[i];
      const blackMove = moves[i + 1];
      
      const numStr = String(moveNumber).padStart(numWidth);
      const whiteYugos = whiteMove.yugos ?? 0;
      const whiteStr = (getNotation(whiteMove.col, whiteMove.row) + '*'.repeat(whiteYugos)).padEnd(6);
      
      let line = `${numStr}. ${whiteStr}`;
      if (blackMove) {
        const blackYugos = blackMove.yugos ?? 0;
        const blackStr = getNotation(blackMove.col, blackMove.row) + '*'.repeat(blackYugos);
        line += `  ${blackStr}`;
      }
      
      lines.push(line);
    }
    
    return lines.join('\n');
  };

  const copyMoveHistory = async () => {
    try {
      const moveHistoryText = formatMoveHistoryAsText();
      if (!moveHistoryText) {
        setCopyNotification('No moves to copy');
        setTimeout(() => setCopyNotification(null), 2000);
        return;
      }
      
      await navigator.clipboard.writeText(moveHistoryText);
      setCopyNotification('Move history copied to clipboard!');
      setTimeout(() => setCopyNotification(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      try {
        const moveHistoryText = formatMoveHistoryAsText();
        const textArea = document.createElement('textarea');
        textArea.value = moveHistoryText;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopyNotification('Move history copied to clipboard!');
        setTimeout(() => setCopyNotification(null), 2000);
      } catch (fallbackErr) {
        setCopyNotification('Failed to copy. Please try selecting the text manually.');
        setTimeout(() => setCopyNotification(null), 3000);
      }
    }
  };

  // Hold-to-scroll functionality for buttons
  const startHoldScroll = (direction: 'prev' | 'next', event?: React.MouseEvent | React.TouchEvent) => {
    event?.preventDefault();
    
    // Clear any existing timeouts/intervals
    if (holdScrollInterval) {
      clearTimeout(holdScrollInterval);
      clearInterval(holdScrollInterval);
      setHoldScrollInterval(null);
    }
    if (holdScrollTimeoutRef.current) {
      clearTimeout(holdScrollTimeoutRef.current);
      holdScrollTimeoutRef.current = null;
    }
    
    // Record start time
    holdStartTimeRef.current = Date.now();
    
    const scrollFunction = direction === 'prev' ? goToPrevious : goToNext;
    
    // Wait 0.5 seconds before executing first move and starting continuous scroll
    holdScrollTimeoutRef.current = setTimeout(() => {
      // Execute first move after 0.5 seconds
      scrollFunction();
      
      // Then continue scrolling every 333ms (3 moves per second)
      const interval = setInterval(() => {
        scrollFunction();
      }, 333);
      
      setHoldScrollInterval(interval);
    }, 500);
  };

  const stopHoldScroll = (direction: 'prev' | 'next', event?: React.MouseEvent | React.TouchEvent) => {
    event?.preventDefault();
    
    const scrollFunction = direction === 'prev' ? goToPrevious : goToNext;
    
    // Check if this was a quick click (< 0.5 seconds)
    if (holdStartTimeRef.current !== null) {
      const holdDuration = Date.now() - holdStartTimeRef.current;
      
      if (holdDuration < 500) {
        // Quick click: execute ONE move
        if (holdScrollTimeoutRef.current) {
          clearTimeout(holdScrollTimeoutRef.current);
          holdScrollTimeoutRef.current = null;
        }
        scrollFunction();
      }
      
      holdStartTimeRef.current = null;
    }
    
    // Clear any existing timeout or interval
    if (holdScrollTimeoutRef.current) {
      clearTimeout(holdScrollTimeoutRef.current);
      holdScrollTimeoutRef.current = null;
    }
    if (holdScrollInterval) {
      clearInterval(holdScrollInterval);
      setHoldScrollInterval(null);
    }
  };

  // Cleanup hold interval when component unmounts
  useEffect(() => {
    return () => {
      if (holdScrollInterval) {
        clearTimeout(holdScrollInterval);
        clearInterval(holdScrollInterval);
      }
      if (holdScrollTimeoutRef.current) {
        clearTimeout(holdScrollTimeoutRef.current);
      }
    };
  }, [holdScrollInterval]);

  // Keyboard navigation for replay with hold-to-scroll
  useEffect(() => {
    let keyHoldInterval: NodeJS.Timeout | null = null;
    let keyHoldTimeout: NodeJS.Timeout | null = null;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys when replay modal is open
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); // Prevent default scrolling
        
        // Clear any existing intervals
        if (keyHoldInterval) {
          clearInterval(keyHoldInterval);
          keyHoldInterval = null;
        }
        if (keyHoldTimeout) {
          clearTimeout(keyHoldTimeout);
          keyHoldTimeout = null;
        }

        const direction = event.key === 'ArrowLeft' ? 'prev' : 'next';

        // Immediate first move
        if (direction === 'prev' && currentMoveIndex > -1) {
          setCurrentMoveIndex(currentMoveIndex - 1);
        } else if (direction === 'next' && currentMoveIndex < moves.length - 1) {
          setCurrentMoveIndex(currentMoveIndex + 1);
        }

        // Wait 0.5 seconds before starting continuous scroll
        keyHoldTimeout = setTimeout(() => {
          // Then continue scrolling every 333ms (3 moves per second)
          keyHoldInterval = setInterval(() => {
            // Use ref to get current value (avoids closure issues)
            const currentMove = currentMoveIndexRef.current;
            if (direction === 'prev') {
              if (currentMove > -1) {
                goToMove(currentMove - 1);
              } else {
                if (keyHoldInterval) {
                  clearInterval(keyHoldInterval);
                  keyHoldInterval = null;
                }
              }
            } else {
              if (currentMove < moves.length - 1) {
                goToMove(currentMove + 1);
              } else {
                if (keyHoldInterval) {
                  clearInterval(keyHoldInterval);
                  keyHoldInterval = null;
                }
              }
            }
          }, 333);
        }, 500);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Clear intervals when key is released
        if (keyHoldInterval) {
          clearInterval(keyHoldInterval);
          keyHoldInterval = null;
        }
        if (keyHoldTimeout) {
          clearTimeout(keyHoldTimeout);
          keyHoldTimeout = null;
        }
      }
    };

    // Add event listeners
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (keyHoldInterval) {
        clearInterval(keyHoldInterval);
      }
      if (keyHoldTimeout) {
        clearTimeout(keyHoldTimeout);
      }
    };
  }, [currentMoveIndex, moves.length]); // Dependencies

  const getResultColor = () => {
    if (result === 'win') return '#28a745';
    if (result === 'loss') return '#dc3545';
    return '#ffc107';
  };

  const getResultText = () => {
    if (result === 'win') return '🏆 Victory';
    if (result === 'loss') return '💔 Defeat';
    return '🤝 Draw';
  };

  const getWinTypeText = (): string => {
    if (result === 'draw') return '';
    
    // Check win_type first (Igo/Wego)
    if (winType === 'igo') return 'Igo';
    if (winType === 'wego') return 'Wego';
    
    // Check game_end_reason for other endings
    if (gameEndReason) {
      const reason = gameEndReason.toLowerCase();
      if (reason.includes('resign')) return 'Resignation';
      if (reason.includes('abandon')) return 'Abandon';
      if (reason.includes('disconnect')) return 'Disconnect';
      if (reason.includes('timeout')) return 'Timeout';
    }
    
    return '';
  };

  if (loading) {
    return (
      <div className="game-replay-overlay" onClick={onClose}>
        <div className="game-replay-modal" onClick={(e) => e.stopPropagation()}>
          <div className="game-replay-loading">Loading game replay...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-replay-overlay" onClick={onClose}>
        <div className="game-replay-modal" onClick={(e) => e.stopPropagation()}>
          <div className="game-replay-error">
            <p>❌ {error}</p>
            <button onClick={onClose} className="game-replay-close-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-replay-overlay" onClick={onClose}>
      <div className="game-replay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="game-replay-header">
          <div className="game-replay-title">
            <h2>Game Replay</h2>
            <div className="game-replay-info">
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                <span>vs <strong>{opponent}</strong></span>
                <span style={{ color: getResultColor(), fontSize: '0.9rem' }}>
                  {getResultText()}
                  {getWinTypeText() && (
                    <span style={{ marginLeft: '6px', fontWeight: 'normal', opacity: 0.9 }}>
                      ({getWinTypeText()})
                    </span>
                  )}
                </span>
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{formatGameTimestamp(timestamp, 'date')}</span>
                <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>{formatGameTimestamp(timestamp, 'time')}</span>
              </span>
            </div>
          </div>
          <button className="game-replay-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="game-replay-content">
          <div className="game-replay-board-container">
            <div className="game-replay-board">
              {board.map((row, rowIndex) => (
                <div key={rowIndex} className="game-replay-row">
                  {row.map((cell, colIndex) => (
                    <div
                      key={colIndex}
                      className={`game-replay-cell ${
                        currentMoveIndex >= 0 &&
                        moves[currentMoveIndex]?.row === rowIndex &&
                        moves[currentMoveIndex]?.col === colIndex
                          ? 'last-move'
                          : ''
                      } ${
                        igoCells.has(`${rowIndex},${colIndex}`) ? 'igo-cell' : ''
                      } ${
                        historyHoverIndex !== null &&
                        moves[historyHoverIndex]?.row === rowIndex &&
                        moves[historyHoverIndex]?.col === colIndex
                          ? 'history-hover'
                          : ''
                      }`}
                      title={
                        cell
                          ? `${cell.color}${cell.isYugo ? ' Yugo' : ''}`
                          : 'Empty'
                      }
                    >
                      {cell && (
                        <>
                          {/* Render the piece as a disc */}
                          <div className={`dot ${cell.color}`} />
                          {cell.isYugo && (
                            <div className="yugo standard" />
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="game-replay-controls">
              <button
                onClick={goToStart}
                disabled={currentMoveIndex === -1}
                className="replay-btn"
              >
                ⏮ First
              </button>
              <button
                onMouseDown={(e) => startHoldScroll('prev', e)}
                onMouseUp={(e) => stopHoldScroll('prev', e)}
                onMouseLeave={(e) => stopHoldScroll('prev', e)}
                onTouchStart={(e) => startHoldScroll('prev', e)}
                onTouchEnd={(e) => stopHoldScroll('prev', e)}
                disabled={currentMoveIndex === -1}
                className="replay-btn"
                title="Previous Move (Hold to scroll)"
              >
                ⏪ Previous
              </button>
              <button
                onMouseDown={(e) => startHoldScroll('next', e)}
                onMouseUp={(e) => stopHoldScroll('next', e)}
                onMouseLeave={(e) => stopHoldScroll('next', e)}
                onTouchStart={(e) => startHoldScroll('next', e)}
                onTouchEnd={(e) => stopHoldScroll('next', e)}
                disabled={currentMoveIndex >= moves.length - 1}
                className="replay-btn"
                title="Next Move (Hold to scroll)"
              >
                Next ⏩
              </button>
              <button
                onClick={goToEnd}
                disabled={currentMoveIndex >= moves.length - 1}
                className="replay-btn"
              >
                Last ⏭
              </button>
              {moves.length > 0 && (
                <button
                  className="replay-btn"
                  onClick={copyMoveHistory}
                  title="Copy move history to clipboard"
                  style={{
                    width: 'auto',
                    minWidth: '36px',
                    maxWidth: 'none',
                    padding: '8px 12px',
                    fontSize: '0.9rem',
                    lineHeight: '1',
                    margin: '0 2px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📋 Copy
                </button>
              )}
            </div>
          </div>
          {copyNotification && (
            <div 
              className="game-replay-copy-notification"
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: copyNotification.includes('Failed') ? '#dc3545' : '#28a745',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '0.9rem',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                whiteSpace: 'nowrap',
                animation: 'fadeIn 0.3s ease'
              }}
            >
              {copyNotification}
            </div>
          )}

          <div className="game-replay-sidebar">
            <h3>Move History</h3>
            <div className="move-list" onMouseLeave={() => setHistoryHoverIndex(null)}>
              {Array.from({ length: Math.ceil(moves.length / 2) }, (_, pairIndex) => {
                const whiteMove = moves[pairIndex * 2];
                const blackMove = moves[pairIndex * 2 + 1];
                const moveNumber = pairIndex + 1;
                const isWhiteActive = whiteMove && currentMoveIndex === pairIndex * 2;
                const isBlackActive = blackMove && currentMoveIndex === pairIndex * 2 + 1;
                
                return (
                  <div
                    key={pairIndex}
                    ref={(isWhiteActive || isBlackActive) ? activeMoveRef : null}
                    className={`move-entry ${isWhiteActive || isBlackActive ? 'active' : ''}`}
                  >
                    <span className="move-number">{moveNumber}.</span>
                    <span
                      className={`move-white ${isWhiteActive ? 'active' : ''} ${historyHoverIndex === pairIndex * 2 ? 'history-hover-move' : ''}`}
                      onClick={() => whiteMove && goToMove(pairIndex * 2)}
                      onMouseEnter={() => {
                        if (whiteMove) setHistoryHoverIndex(pairIndex * 2);
                      }}
                      onMouseLeave={() => setHistoryHoverIndex(null)}
                      style={{ cursor: whiteMove ? 'pointer' : 'default' }}
                    >
                      {whiteMove ? (
                        <>
                          {(() => {
                            // Ensure we have valid numbers
                            const col = Number(whiteMove.col);
                            const row = Number(whiteMove.row);
                            const notation = getNotation(col, row);
                            
                            // Debug logging for moves without yugos (where the issue occurs)
                            if (!whiteMove.yugos || whiteMove.yugos === 0) {
                              const colLetter = String.fromCharCode(97 + col).toUpperCase();
                              const rowNumber = 8 - row;
                              console.log(`[DEBUG] Rendering white move ${pairIndex * 2} (NO YUGOS):`);
                              console.log('  - col (raw):', whiteMove.col, 'type:', typeof whiteMove.col);
                              console.log('  - col (num):', col);
                              console.log('  - row (raw):', whiteMove.row, 'type:', typeof whiteMove.row);
                              console.log('  - row (num):', row);
                              console.log('  - colLetter calc:', colLetter, '(from col', col, ')');
                              console.log('  - rowNumber calc:', rowNumber, '(from row', row, ', 8-row=', 8-row, ')');
                              console.log('  - Final notation:', notation, 'length:', notation.length);
                              console.log('  - yugos:', whiteMove.yugos);
                              console.log('  - Full move object:', JSON.stringify(whiteMove));
                            }
                            
                            return notation;
                          })()}
                          {((whiteMove.yugos ?? 0) > 0) ? <span className="yugo-indicator">{'*'.repeat(whiteMove.yugos ?? 0)}</span> : null}
                        </>
                      ) : ''}
                    </span>
                    <span
                      className={`move-black ${isBlackActive ? 'active' : ''} ${historyHoverIndex === pairIndex * 2 + 1 ? 'history-hover-move' : ''}`}
                      onClick={() => blackMove && goToMove(pairIndex * 2 + 1)}
                      onMouseEnter={() => {
                        if (blackMove) setHistoryHoverIndex(pairIndex * 2 + 1);
                      }}
                      onMouseLeave={() => setHistoryHoverIndex(null)}
                      style={{ cursor: blackMove ? 'pointer' : 'default' }}
                    >
                      {blackMove ? (
                        <>
                          {(() => {
                            const col = Number(blackMove.col);
                            const row = Number(blackMove.row);
                            const notation = getNotation(col, row);
                            return notation;
                          })()}
                          {((blackMove.yugos ?? 0) > 0) ? <span className="yugo-indicator">{'*'.repeat(blackMove.yugos ?? 0)}</span> : null}
                        </>
                      ) : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameReplay;

